import { BodyPrimary } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { Controller, useForm } from 'react-hook-form';
import { CurrentOpenMenu } from '@/pages/SearchPageSimplified/types';
import { Distance } from '@/types/Distance';

import { FilterAction } from '@/types/SearchFacet';
import {
    modifyParamsRemoveCountryAndDistance,
    modifyParamsRemovePage,
    modifyParamsWithDistance,
    parseQueryParams,
} from '@/utils/queryParams';
import { OptionType, Select } from '@liveauctioneers/hammer-ui-core/select';
import { SearchInput } from '@liveauctioneers/hammer-ui-core/searchInput';
import { toast } from '@liveauctioneers/hammer-ui-core/toast';
import { useLocation } from 'react-router-dom';
import { useNavigateSearchPageWithParams } from '@/pages/SearchPageSimplified/hooks/useNavigateSearchPageWithParams';

const DISTANCE_OPTIONS = [20, 40, 60, 80, 100, 120, 250, 500, 1000];
const distanceOptions: OptionType[] = DISTANCE_OPTIONS.map((option) => {
    return { label: `${option} miles`, value: option };
});

type FormData = {
    distanceRange: OptionType | null;
    searchZipCode: string;
};
type Props = {
    distance: Distance;
    isAllFilters?: boolean;
};

export const LocationRangeFilter = ({ distance, isAllFilters }: Props) => {
    const { search } = useLocation();
    const queryParams = parseQueryParams(search);
    const navigateWithParams = useNavigateSearchPageWithParams();

    const { control, setValue, watch } = useForm<FormData>({
        defaultValues: {
            distanceRange: distanceOptions.find((option) => option.value === distance?.space) ?? distanceOptions[0],
            searchZipCode: distance?.zipCode ?? '',
        },
    });

    const zipCodeWatch = watch('searchZipCode');
    const distanceRangeWatch = watch('distanceRange');

    const keepMenuOpenValue = isAllFilters ? CurrentOpenMenu.AllFilters : CurrentOpenMenu.Location;

    const onSubmit = (zipCode: string) => {
        if (!zipCode.length) {
            return;
        }
        if (zipCode.length !== 5) {
            toast('warning', { title: 'Enter a valid zip code.' });
            return;
        }

        const country = 'US';
        const measurement = 'mile';
        let newParams = modifyParamsWithDistance(queryParams, country, zipCode, distanceRangeWatch.value, measurement);
        newParams = modifyParamsRemovePage(newParams);

        navigateWithParams({
            checkForPageRemoval: true,
            currentOpenMenu: keepMenuOpenValue,
            filterAction: FilterAction.LocationChanged,
            params: newParams,
            preventScrollReset: true,
        });
    };

    const handleClear = () => {
        let newParams = modifyParamsRemoveCountryAndDistance(queryParams);
        newParams = modifyParamsRemovePage(newParams);
        navigateWithParams({
            checkForPageRemoval: true,
            currentOpenMenu: keepMenuOpenValue,
            filterAction: FilterAction.LocationRemoved,
            params: newParams,
            preventScrollReset: true,
        });
    };

    return (
        <div className={cn('grid grid-cols-[minmax(128px,1fr)_min-content_minmax(128px,1fr)] items-end gap-xs')}>
            <Controller
                control={control}
                name="distanceRange"
                render={({ field }) => (
                    <Select
                        {...field}
                        aria-description="Choose the maximum distance range for item location search"
                        aria-label="Select distance range"
                        data-testid="location-distance-select"
                        instanceId="distance-range"
                        labelText="Within"
                        name="distanceRange"
                        options={distanceOptions}
                        small
                        usePortalMenu
                    />
                )}
                rules={{ required: true }}
            />
            <BodyPrimary className="mb-2xs">of</BodyPrimary>
            <SearchInput
                onClear={handleClear}
                onSubmit={onSubmit}
                onTextChange={(searchTerm) => {
                    setValue('searchZipCode', searchTerm);
                    if (distance?.zipCode && searchTerm.length === 0) {
                        handleClear();
                    }
                }}
                placeholder="Zip code"
                small
                value={zipCodeWatch}
            />
        </div>
    );
};
